import { useMemo, useEffect, useCallback, useReducer } from 'react'

import { EFilterKeys } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { FilterItem, CheckboxOption, UpdateQueryParams, SelectedFilter } from '../menu/filterSection.interface'
import { calculateTotalSelectedFilters } from '../menu/filterUtils'

// Define the state interface
interface FilterState {
  selectedFiltersState: Record<string, string[]>
  collapseActiveKeys: string[]
}

// Define action types
type FilterAction =
  | { type: 'SET_FILTER_VALUES'; filterType: string; values: string[] }
  | { type: 'TOGGLE_FILTER_VALUE'; filterType: string; value: string }
  | { type: 'REMOVE_FILTER_VALUE'; filterType: string; value: string }
  | { type: 'CLEAR_ALL_FILTERS' }
  | { type: 'SET_COLLAPSE_KEYS'; keys: string[] }
  | { type: 'RESET_COLLAPSE_STATE' }
  | { type: 'SYNC_SELECTED_FILTERS'; selectedFilters: SelectedFilter }

// Initial state
const initialFilterState: FilterState = {
  selectedFiltersState: {},
  collapseActiveKeys: [],
}

// Reducer function
const filterReducer = (state: FilterState, action: FilterAction): FilterState => {
  switch (action.type) {
    case 'SET_FILTER_VALUES':
      return {
        ...state,
        [`selected${action.filterType.charAt(0).toUpperCase() + action.filterType.slice(1).replace(/\s+/g, '')}`]:
          action.values,
      }

    case 'TOGGLE_FILTER_VALUE': {
      const filterKey =
        `selected${action.filterType.charAt(0).toUpperCase() + action.filterType.slice(1).replace(/\s+/g, '')}` as keyof FilterState

      // Safely handle potentially undefined values by defaulting to an empty array
      const currentValues = Array.isArray(state[filterKey]) ? state[filterKey] : []
      const valueExists = currentValues.includes(action.value)

      return {
        ...state,
        [filterKey]: valueExists
          ? currentValues.filter(value => value !== action.value)
          : [...currentValues, action.value],
      }
    }

    case 'REMOVE_FILTER_VALUE': {
      const filterKey =
        `selected${action.filterType.charAt(0).toUpperCase() + action.filterType.slice(1).replace(/\s+/g, '')}` as keyof FilterState

      // Safely handle potentially undefined values
      const currentValues = Array.isArray(state[filterKey]) ? state[filterKey] : []

      return {
        ...state,
        [filterKey]: currentValues.filter(value => value !== action.value),
      }
    }

    case 'CLEAR_ALL_FILTERS':
      return {
        ...state,
        selectedFiltersState: {},
      }

    case 'SET_COLLAPSE_KEYS':
      return {
        ...state,
        collapseActiveKeys: action.keys,
      }

    case 'RESET_COLLAPSE_STATE':
      return {
        ...state,
        collapseActiveKeys: [],
      }

    case 'SYNC_SELECTED_FILTERS': {
      // Handle undefined values by defaulting to an empty array
      const newState = {
        ...state,
        selectedFiltersState: Object.fromEntries(
          Object.entries(action.selectedFilters).map(([key, value]) => [key, value ?? []]),
        ),
      }

      return newState
    }

    default:
      return state
  }
}

export interface UseFilterStateReturn {
  selectedFiltersState: Record<string, string[]>
  selectedFilterChips: { label: string; filterType: string; value: string }[]
  totalSelectedFilters: number
  collapseActiveKeys: string[]
  handleCollapseChange: (activeKeys: string[]) => void
  resetCollapseState: () => void
  clearAllFilters: () => void
  handleCheckboxFilterChange: (filter: FilterItem, selectedOptions: CheckboxOption[]) => void
  handleColorChange: (filter: FilterItem, value: string | string[]) => void
  handleChipDismiss: (filterType: string, value: string) => void
  handleChipDismissByLabel: (label: string) => void
}

export const useFilterState = (
  selectedFilters: SelectedFilter,
  updateQuery: (params: UpdateQueryParams) => void = () => {
    /* no-op */
  },
): UseFilterStateReturn => {
  // Use reducer for state management
  const [state, dispatch] = useReducer(filterReducer, initialFilterState)

  // Extract state variables for convenience and backward compatibility
  const { selectedFiltersState, collapseActiveKeys } = state

  // Create chips for selected filters - this is now derived from our consolidated state
  const selectedFilterChips = useMemo(() => {
    const chips: { label: string; filterType: string; value: string }[] = []

    Object.entries(selectedFiltersState).forEach(([filterType, state]) => {
      if (state.length > 0) {
        state.forEach(value => {
          chips.push({
            label: value,
            filterType: filterType,
            value,
          })
        })
      }
    })

    return chips
  }, [selectedFiltersState])

  // Calculate total selected filters
  const totalSelectedFilters = useMemo(() => {
    return calculateTotalSelectedFilters(selectedFilters)
  }, [selectedFilters])

  // Sync state with selectedFilters prop
  useEffect(() => {
    dispatch({
      type: 'SYNC_SELECTED_FILTERS',
      selectedFilters: selectedFilters,
    })
  }, [selectedFilters])

  // Function to clear all filters - now uses our reducer
  const clearAllFilters = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL_FILTERS' })
    // Use updateQuery to reset all filters
    updateQuery({
      filter: { type: 'reset' },
    })
  }, [updateQuery])

  // Helper function to handle checkbox filter changes
  const handleCheckboxFilterChange = useCallback(
    (filter: FilterItem, selectedOptions: CheckboxOption[]) => {
      const filterName = filter.name
      const filterLabel = filter.config.label
      const selectedNames = selectedOptions
        .map(option => option.name)
        .filter((name): name is string => name !== undefined)
      const state = selectedFiltersState[filterName] ?? []

      // Find values to add and remove
      const valuesToAdd = selectedNames.filter(name => !state.includes(name))
      const valuesToRemove = state.filter(name => !selectedNames.includes(name))

      // Update local state using our reducer
      dispatch({ type: 'SET_FILTER_VALUES', filterType: filterName, values: selectedNames })

      // Apply changes using updateQuery
      if (valuesToAdd.length > 0) {
        valuesToAdd.forEach(value => {
          const filterKey = filterName === EFilterKeys.SUBCOLLECTIONS ? EFilterKeys.PRODUCT_TYPE : filterName
          trackEvent(TELEMETRY_EVENT_NAMES.FILTER_APPLIED, {
            filter_key: filterKey,
            filter_value: value,
          })

          updateQuery({
            filter: {
              type: 'toggle',
              name: filterName,
              value,
              label: filterLabel,
            },
          })
        })
      }

      if (valuesToRemove.length > 0) {
        valuesToRemove.forEach(value => {
          updateQuery({
            filter: {
              type: 'remove',
              name: filterName,
              value,
            },
          })
        })
      }
    },
    [selectedFiltersState, updateQuery],
  )

  // Helper function to handle color filter changes
  const handleColorChange = useCallback(
    (filter: FilterItem, value: string | string[]) => {
      const valueToUse = typeof value === 'string' ? value : (value[0] ?? '')
      const isSelected = selectedFiltersState[filter.name]?.includes(valueToUse)

      if (isSelected) {
        dispatch({ type: 'REMOVE_FILTER_VALUE', filterType: filter.name, value: valueToUse })

        updateQuery({
          filter: {
            type: 'remove',
            name: filter.name,
            value: valueToUse,
          },
        })
      } else {
        const filterName = filter.name === EFilterKeys.SUBCOLLECTIONS ? EFilterKeys.PRODUCT_TYPE : filter.name
        trackEvent(TELEMETRY_EVENT_NAMES.FILTER_APPLIED, {
          filter_key: filterName,
          filter_value: valueToUse,
        })

        dispatch({ type: 'TOGGLE_FILTER_VALUE', filterType: filter.name, value: valueToUse })

        updateQuery({
          filter: {
            type: 'toggle',
            name: filter.name,
            value: valueToUse,
            label: filter.config.label,
          },
        })
      }
    },
    [selectedFiltersState, updateQuery],
  )

  // Handle chip dismiss
  const handleChipDismiss = useCallback(
    (filterType: string, value: string) => {
      const state = selectedFiltersState[filterType]
      if (state) {
        // Update local state using our reducer
        dispatch({ type: 'REMOVE_FILTER_VALUE', filterType, value })

        // Apply changes using updateQuery
        updateQuery({
          filter: {
            type: 'remove',
            name: filterType,
            value,
          },
        })
      }
    },
    [selectedFiltersState, updateQuery],
  )

  // Function to handle chip dismiss by label
  const handleChipDismissByLabel = useCallback(
    (value: string) => {
      const chip = selectedFilterChips.find(c => c.value === value)
      if (chip) {
        handleChipDismiss(chip.filterType, chip.value)
      }
    },
    [selectedFilterChips, handleChipDismiss],
  )

  // Collapse state handlers
  const handleCollapseChange = useCallback((activeKeys: string[]) => {
    dispatch({ type: 'SET_COLLAPSE_KEYS', keys: activeKeys })
  }, [])

  const resetCollapseState = useCallback(() => {
    dispatch({ type: 'RESET_COLLAPSE_STATE' })
  }, [])

  return {
    // State - now coming from our reducer
    selectedFiltersState,
    selectedFilterChips,
    totalSelectedFilters,
    collapseActiveKeys,

    // Actions - now using our reducer
    clearAllFilters,
    handleCheckboxFilterChange,
    handleColorChange,
    handleChipDismiss,
    handleChipDismissByLabel,
    handleCollapseChange,
    resetCollapseState,
  }
}
